export const MAX_RECORDING_SECONDS = 10 * 60;
const MAX_RECORDING_BYTES = 100 * 1024 * 1024;
export type RecordingState = "idle" | "requesting" | "recording" | "preparing";

// A WAV has a duration and seekable header even when the browser's recorded
// WebM does not. This also gives every processor the same input format.
export function recordingWav(audio: AudioBuffer): File {
  if (!audio.numberOfChannels || !audio.length || !Number.isFinite(audio.sampleRate) || audio.sampleRate <= 0) {
    throw new Error("The recording was empty. Try recording again.");
  }
  const frames = Math.min(audio.length, Math.floor(audio.sampleRate * MAX_RECORDING_SECONDS));
  if (frames / audio.sampleRate < 0.1) throw new Error("That recording was too short. Record for at least a moment, then stop.");
  if (frames * 2 + 44 > MAX_RECORDING_BYTES) throw new Error("The recording is too large. Try a shorter recording.");
  const bytes = new ArrayBuffer(44 + frames * 2);
  const view = new DataView(bytes);
  const text = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i++) view.setUint8(offset + i, value.charCodeAt(i));
  };
  text(0, "RIFF");
  view.setUint32(4, 36 + frames * 2, true);
  text(8, "WAVEfmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, audio.sampleRate, true);
  view.setUint32(28, audio.sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, frames * 2, true);
  const channels = Array.from({ length: audio.numberOfChannels }, (_, i) => audio.getChannelData(i));
  for (let i = 0; i < frames; i++) {
    let sample = 0;
    for (const channel of channels) sample += channel[i]! / channels.length;
    sample = Math.max(-1, Math.min(1, sample));
    view.setInt16(44 + i * 2, Math.round(sample * (sample < 0 ? 32768 : 32767)), true);
  }
  return new File([bytes], "voice-recording.wav", { type: "audio/wav" });
}

export async function prepareVoiceRecording(blob: Blob): Promise<File> {
  const context = new OfflineAudioContext(1, 1, 48000);
  const audio = await context.decodeAudioData(await blob.arrayBuffer());
  return recordingWav(audio);
}

function microphoneError(error: unknown): string {
  const name = error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "Microphone access was blocked. Allow microphone access in your browser's site settings, or choose an audio file.";
  if (name === "NotFoundError") return "No microphone was found. Connect one and try again, or choose an audio file.";
  if (name === "NotReadableError" || name === "AbortError") return "Your microphone could not be used. Check whether another app is using it, then try again.";
  return "We could not start recording. Try again, or choose an audio file.";
}

interface RecordingOptions {
  onState: (state: RecordingState) => void;
  onTime: (seconds: number) => void;
  onError: (message: string) => void;
  onRecording: (file: File) => Promise<void>;
}

interface RecordingDependencies {
  getUserMedia: () => Promise<MediaStream>;
  createRecorder: (stream: MediaStream) => MediaRecorder;
  prepare: (blob: Blob) => Promise<File>;
}

export class VoiceRecording {
  state: RecordingState = "idle";
  private version = 0;
  private disposed = false;
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private options: RecordingOptions;
  private dependencies: RecordingDependencies;

  constructor(options: RecordingOptions, dependencies: RecordingDependencies = {
    getUserMedia: () => navigator.mediaDevices.getUserMedia({ audio: { channelCount: { ideal: 1 }, echoCancellation: true, noiseSuppression: true }, video: false }),
    createRecorder: (stream) => {
      const mimeType = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus", "audio/webm"].find(type => MediaRecorder.isTypeSupported(type));
      return new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    },
    prepare: prepareVoiceRecording,
  }) {
    this.options = options;
    this.dependencies = dependencies;
  }

  private setState(state: RecordingState): void {
    this.state = state;
    this.options.onState(state);
  }

  private releaseMicrophone(): void {
    for (const track of this.stream?.getTracks() || []) {
      track.onended = null;
      track.stop();
    }
    this.stream = null;
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  async start(): Promise<void> {
    if (this.disposed || this.state !== "idle") return;
    const version = ++this.version;
    this.setState("requesting");
    try {
      const stream = await this.dependencies.getUserMedia();
      if (version !== this.version) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }
      this.stream = stream;
      const recorder = this.dependencies.createRecorder(stream);
      this.recorder = recorder;
      const chunks: Blob[] = [];
      let bytes = 0;
      recorder.ondataavailable = (event) => {
        if (version !== this.version || !event.data.size) return;
        bytes += event.data.size;
        if (bytes > MAX_RECORDING_BYTES) {
          this.fail("The recording is too large. Try a shorter recording.");
          return;
        }
        chunks.push(event.data);
      };
      recorder.onerror = () => this.fail("Recording was interrupted. Try recording again, or choose an audio file.");
      recorder.onstop = async () => {
        if (version !== this.version) return;
        this.recorder = null;
        this.releaseMicrophone();
        this.setState("preparing");
        try {
          if (!bytes) throw new Error("The recording was empty. Try recording again.");
          const blob = new Blob(chunks, { type: recorder.mimeType || chunks[0]?.type });
          chunks.length = 0;
          const file = await this.dependencies.prepare(blob);
          if (version !== this.version) return;
          await this.options.onRecording(file);
          if (version === this.version) this.setState("idle");
        } catch {
          if (version === this.version) this.fail("We could not prepare that recording. Try recording for a few seconds, or choose an audio file.");
        }
      };
      recorder.start(1000);
      const started = Date.now();
      this.options.onTime(0);
      this.setState("recording");
      for (const track of stream.getAudioTracks()) track.onended = () => this.stop();
      this.timer = setInterval(() => {
        const elapsed = (Date.now() - started) / 1000;
        this.options.onTime(Math.min(elapsed, MAX_RECORDING_SECONDS));
        if (elapsed >= MAX_RECORDING_SECONDS) this.stop();
      }, 250);
    } catch (error) {
      if (version === this.version) this.fail(microphoneError(error));
    }
  }

  stop(): void {
    if (this.state !== "recording" || !this.recorder) return;
    this.setState("preparing");
    try {
      this.recorder.stop();
      this.releaseMicrophone();
    } catch {
      this.fail("We could not finish that recording. Try recording again.");
    }
  }

  cancel(): void {
    ++this.version;
    const recorder = this.recorder;
    this.recorder = null;
    if (recorder) {
      recorder.ondataavailable = recorder.onstop = recorder.onerror = null;
      try {
        if (recorder.state !== "inactive") recorder.stop();
      } catch {
        // Tracks must still be released if the device has already disappeared.
      }
    }
    this.releaseMicrophone();
    this.setState("idle");
  }

  private fail(message: string): void {
    this.cancel();
    this.options.onError(message);
  }

  dispose(): void {
    this.disposed = true;
    this.cancel();
  }
}
